import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { PhoneInput } from '../components/ui/phone-input';
import { 
  Loader2, 
  Users, 
  Wallet, 
  FileText, 
  CalendarDays,
  ClipboardList, 
  Clock, 
  Newspaper, 
  TrendingUp,
  ArrowRight,
  CheckCircle2,
  Shield
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/* ─── animated node-grid canvas ─── */
const NodeGrid = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let nodes = [];
    const NODE_COUNT = 50;
    const CONNECT_DIST = 140;

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    const init = () => {
      resize();
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      nodes = Array.from({ length: NODE_COUNT }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 2 + 1,
      }));
    };

    const draw = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);

      // move
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      }

      // lines
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            const alpha = 1 - d / CONNECT_DIST;
            ctx.strokeStyle = `rgba(128,255,167,${alpha * 0.15})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      // dots
      for (const n of nodes) {
        ctx.fillStyle = 'rgba(128,255,167,0.5)';
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }

      animId = requestAnimationFrame(draw);
    };

    init();
    draw();
    window.addEventListener('resize', init);
    return () => {
      window.removeEventListener('resize', init);
      cancelAnimationFrame(animId);
    };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 w-full h-full" />;
};

/* ─── animated feature pill ─── */
const FeaturePill = ({ icon: Icon, title, description, color, delay }) => (
  <div
    className="group flex items-start gap-2.5 p-2.5 rounded-lg border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-emerald-400/30 transition-all duration-300 cursor-default"
    style={{ animation: `fadeSlideUp 0.6s ${delay}s both` }}
  >
    <div className={`p-1.5 rounded-md bg-white/10 ${color} shrink-0`}>
      <Icon className="w-3.5 h-3.5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-semibold text-white/90 group-hover:text-emerald-300 transition-colors">{title}</p>
      <p className="text-[10px] text-white/45 mt-0.5 leading-snug">{description}</p>
    </div>
  </div>
);

/* ─── main login page ─── */
const LoginPage = () => {
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { setMounted(true); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!phone || !phone.trim()) { setError('Please enter your phone number'); setLoading(false); return; }
    if (!pin || !pin.trim()) { setError('Please enter your PIN'); setLoading(false); return; }
    if (pin.length < 4) { setError('PIN must be at least 4 digits'); setLoading(false); return; }

    try {
      let cleanPhone = phone ? phone.replace(/\s/g, '') : '';
      if (cleanPhone.startsWith('+255')) cleanPhone = '0' + cleanPhone.substring(4);
      else if (cleanPhone.startsWith('255')) cleanPhone = '0' + cleanPhone.substring(3);
      const result = await login(cleanPhone, pin);

      if (result.success) {
        navigate('/overview');
      } else {
        const msg = result.error || '';
        if (msg.toLowerCase().includes('locked') || msg.toLowerCase().includes('try again in')) setError(msg);
        else if (msg.includes('Invalid phone number or PIN')) setError('Incorrect phone number or PIN. Please check your credentials and try again.');
        else if (msg.includes('Network') || msg.includes('fetch')) setError('Unable to connect to the server. Please check your internet connection.');
        else if (msg.includes('not found')) setError('Account not found. Please verify your phone number.');
        else if (msg.includes('deactivated')) setError('Account is deactivated. Contact your administrator.');
        else setError(msg || 'Login failed. Please try again.');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    }
    setLoading(false);
  };

  const features = [
    { icon: Users, title: 'Team Profiles', description: 'Employee records & team structure', color: 'text-emerald-400' },
    { icon: Wallet, title: 'Payroll', description: 'Salaries, allowances & deductions', color: 'text-sky-400' },
    { icon: FileText, title: 'Documentation', description: 'Letters, contracts & documents', color: 'text-violet-400' },
    { icon: CalendarDays, title: 'Leave Management', description: 'Leave requests, balances & approvals', color: 'text-amber-400' },
    { icon: ClipboardList, title: 'Tasks & Follow-ups', description: 'Assigned tasks, deadlines & progress', color: 'text-rose-400' },
    { icon: Clock, title: 'Attendance', description: 'Check in, shifts & attendance history', color: 'text-cyan-400' },
    { icon: Newspaper, title: 'Newsroom', description: 'Company updates in one place', color: 'text-pink-400' },
    { icon: TrendingUp, title: 'Analytics', description: 'Track progress & targets in real time', color: 'text-lime-400' },
  ];

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* ── keyframes ── */}
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 30px rgba(128,255,167,0.06); }
          50%      { box-shadow: 0 0 60px rgba(128,255,167,0.12); }
        }
        @keyframes scanLine {
          0%   { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }
        .scan-line {
          position: absolute;
          left: 0; right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent 0%, rgba(128,255,167,0.25) 50%, transparent 100%);
          animation: scanLine 6s linear infinite;
        }
      `}</style>

      {/* ═══════ LEFT — brand panel ═══════ */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #070E24 0%, #0F2E6E 50%, #091B42 100%)' }}>
        {/* animated node grid */}
        <NodeGrid />

        {/* scan line effect */}
        <div className="scan-line" />

        {/* subtle grid overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(128,255,167,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(128,255,167,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />

        {/* content */}
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          {/* top — logo */}
          <div style={{ animation: 'fadeSlideUp 0.5s 0.1s both' }}>
            <div className="flex items-center gap-3">
              <img src="/logo192.png" alt="Desknodes" className="w-9 h-9 rounded-lg" />
              <span className="text-xl font-bold text-white tracking-wide">desknodes</span>
            </div>
          </div>

          {/* middle — hero */}
          <div className="space-y-8 -mt-8">
            <div className="space-y-4">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
                style={{ animation: 'fadeSlideUp 0.5s 0.3s both' }}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Your Digital Workspace
              </div>
              <h1
                className="text-4xl xl:text-5xl font-extrabold text-white leading-[1.15]"
                style={{ animation: 'fadeSlideUp 0.6s 0.4s both' }}
              >
                Work smarter.<br />
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #80FFA7 0%, #4CE8FF 100%)' }}>
                  Stay aligned.
                </span>
              </h1>
              <p
                className="text-base text-white/50 max-w-md leading-relaxed"
                style={{ animation: 'fadeSlideUp 0.6s 0.55s both' }}
              >
                Your daily hub for staying aligned, productive, and connected.
              </p>
            </div>

            {/* features */}
            <div className="grid grid-cols-2 gap-2 max-w-lg">
              {features.map((f, i) => (
                <FeaturePill key={i} {...f} delay={0.7 + i * 0.08} />
              ))}
            </div>
          </div>

          {/* bottom — footer */}
          <div className="flex items-center gap-2 text-white/30 text-xs" style={{ animation: 'fadeIn 1s 1.2s both' }}>
            <Shield className="w-3.5 h-3.5" />
            <span>Secured access &middot; AES-256 encrypted</span>
          </div>
        </div>
      </div>

      {/* ═══════ RIGHT — login form ═══════ */}
      <div className="flex-1 flex items-center justify-center relative bg-[#fafbfd] overflow-hidden">
        {/* subtle radial glow behind card */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(15,46,110,0.08) 0%, transparent 70%)' }}
        />

        <div
          className={`w-full max-w-[400px] mx-6 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {/* mobile logo */}
          <div className="text-center mb-8 lg:hidden">
            <div className="flex items-center justify-center gap-2">
              <img src="/logo192.png" alt="Desknodes" className="w-8 h-8 rounded-lg" />
              <span className="text-xl font-bold" style={{ color: '#0F2E6E' }}>desknodes</span>
            </div>
            <p className="text-sm text-gray-400 mt-2">Your digital workspace</p>
          </div>

          {/* form card */}
          <div
            className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_8px_40px_rgba(15,46,110,0.06)] p-8"
            style={{ animation: 'glowPulse 4s ease-in-out infinite' }}
            data-testid="login-form-card"
          >
            <div className="text-center mb-7">
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Sign In</h2>
              <p className="text-sm text-gray-400 mt-1">Enter your credentials to access your workspace</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <Alert variant="destructive" data-testid="login-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone Number</Label>
                <PhoneInput
                  id="phone"
                  value={phone}
                  onChange={setPhone}
                  placeholder="712345678"
                  disabled={loading}
                  defaultCountry="TZ"
                  data-testid="phone-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pin" className="text-xs font-semibold text-gray-500 uppercase tracking-wider">PIN</Label>
                <Input
                  id="pin"
                  type="password"
                  placeholder="Enter your PIN"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  required
                  disabled={loading}
                  maxLength={4}
                  className="h-11 border-gray-200 focus:border-[#0F2E6E] focus:ring-[#0F2E6E]/20"
                  data-testid="pin-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 text-white font-semibold transition-all duration-200 hover:shadow-lg hover:shadow-[#0F2E6E]/20"
                style={{ background: 'linear-gradient(135deg, #0F2E6E 0%, #1A4292 100%)' }}
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-400">
                Need help? Contact your administrator
              </p>
            </div>
          </div>

          {/* bottom link */}
          <p className="text-center text-[10px] text-gray-300 mt-6 tracking-wider uppercase">
            By Data Farm
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
